import { AutoComplete, Form, Modal, Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ConditionSuggestionEntityTypeEnum from '~Api/Application/ConditionSuggestionEntityTypeEnum';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import IConditionSuggestion from '~Api/Application/IConditionSuggestion';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import {
    applicationConditionSuggestionsListAction,
    applicationConditionsAddAction,
} from '~Applications/actions';
import { applicationConditionSuggestionsSelector } from '~Applications/selectors';
import { workflowStatusLabels } from '~Applications/workflowStatusLabels';

interface IOption {
    label: string;
    score: number;
    value: string;
}

interface IConditionAddErrors {
    conditionName?: string;
}

export default function ConditionAddModal(props: {
    application?: IApplication;
    applicationProperty?: IApplicationProperty;
    applicationBorrower?: IBorrower;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const {
        application,
        applicationBorrower,
        applicationProperty,
        isOpen,
        onCancel,
    } = props;

    const [ conditionName, setConditionName ] = useState<string>(null);
    const [ options, setOptions ] = useState<IOption[]>([]);
    const [ requiredWorkflowStatus, setRequiredWorkflowStatus ] = useState<WorkflowStatusEnum>(WorkflowStatusEnum.FormalApproval);
    const [ errors, setErrors ] = useState<IConditionAddErrors>({});

    const suggestions: IConditionSuggestion[] = useSelector(applicationConditionSuggestionsSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!suggestions) {
            dispatch(applicationConditionSuggestionsListAction());
        }
    }, [
        dispatch,
        suggestions,
    ]);

    const validateConditionName: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (conditionName === null || conditionName === '') {
            error = 'Please enter a name';
        } else if (conditionName.length > 255) {
            error = 'Name must be less than 255 characters';
        }

        setErrors((prevErrors: IConditionAddErrors) => ({
            ...prevErrors,
            conditionName: error,
        }));

        return !error;
    }, [
        conditionName,
    ]);

    const onClickAdd: () => void = useCallback(() => {
        const applicationUuid: string = application ? application.uuid : null;
        const applicationBorrowerUuid: string = applicationBorrower ? applicationBorrower.uuid : null;
        const applicationPropertyUuid: string = applicationProperty ? applicationProperty.uuid : null;

        let valid: boolean = true;

        valid = validateConditionName() && valid;

        if (!valid) {
            return;
        }

        dispatch(applicationConditionsAddAction(
            applicationUuid,
            applicationBorrowerUuid,
            applicationPropertyUuid,
            conditionName,
            requiredWorkflowStatus,
        ));

        setConditionName(null);
        setRequiredWorkflowStatus(WorkflowStatusEnum.FormalApproval);
        onCancel();
    }, [
        application,
        applicationBorrower,
        applicationProperty,
        conditionName,
        dispatch,
        onCancel,
        requiredWorkflowStatus,
        validateConditionName,
    ]);

    const onSearchConditionName: (value: string) => void = useCallback((value: string) => {
        const searchValue: string = value.toLocaleLowerCase();

        let entityType: ConditionSuggestionEntityTypeEnum = ConditionSuggestionEntityTypeEnum.Application;
        if (applicationBorrower) {
            entityType = ConditionSuggestionEntityTypeEnum.Borrower;
        } else if (applicationProperty) {
            entityType = ConditionSuggestionEntityTypeEnum.Property;
        }

        const results: IOption[] = [];

        _.each(suggestions, (suggestion: IConditionSuggestion) => {
            if (suggestion.entityType !== entityType) {
                return;
            }

            if (suggestion.name.toLocaleLowerCase().includes(searchValue)) {
                results.push({
                    label: suggestion.name,
                    score: suggestion.score,
                    value: suggestion.name,
                });
            }
        });

        setConditionName(value);
        setOptions(_.sortBy(results, ['score']));
    }, [
        applicationBorrower,
        applicationProperty,
        suggestions,
    ]);

    const onSelectConditionName: (value: string) => void = useCallback((value: string) => {
        setConditionName(value);
    }, []);

    const onSelectWorkflowStatus: (requiredWorkflowStatus: WorkflowStatusEnum) => void = useCallback((requiredWorkflowStatus: WorkflowStatusEnum) => {
        setRequiredWorkflowStatus(requiredWorkflowStatus);
    }, []);

    const workflowStatusOptions: DefaultOptionType[] = _.map(workflowStatusLabels, (label: string, workflowStatus: string) => ({
        label,
        value: workflowStatus,
    }));

    if (!suggestions) {
        return null;
    }

    const entityName: string = applicationProperty ? applicationProperty.dealProperty.formattedAddress : (applicationBorrower ? applicationBorrower.dealBorrower.formattedName : application.code);

    return (
        <Modal
            destroyOnClose={true}
            okText='Add Condition'
            onCancel={onCancel}
            onOk={onClickAdd}
            open={isOpen}
            title={`Add Condition for ${entityName}`}
            wrapClassName='application-conditions-add-modal'
            width={800}
        >
            <Form.Item
                label='Name'
                className='condition'
                extra={`${conditionName ? conditionName.length : 0} / 255`}
                help={errors.conditionName}
                validateStatus={errors.conditionName && 'error'}
            >
                <AutoComplete
                    dropdownMatchSelectWidth={300}
                    maxLength={255}
                    onBlur={validateConditionName}
                    onSearch={onSearchConditionName}
                    onSelect={onSelectConditionName}
                    options={_.slice(options, 0, 20)}
                    value={conditionName}
                />
            </Form.Item>
            <Form.Item
                label='Required Before'
                className='workflow-status'
            >
                <Select
                    onChange={onSelectWorkflowStatus}
                    value={requiredWorkflowStatus}
                    options={workflowStatusOptions}
                />
            </Form.Item>
        </Modal>
    );
}
